import type { OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { isNullOrUndefined } from 'is-what';
import type { PaginationInstance } from 'ngx-pagination';

import { StringConstants } from '../../rcr-exchange/constants/string-constants';
import { DownloadXlsxService } from '../../shared/services/download-xlsx.service';
import { FeeDisclaimerService } from '../../shared/services/fee-disclaimer.service';
import { FmLoadingPageService } from '../../shared/services/fm-loading-page.service';
import { UserService } from '../../shared/services/user.service';
import type { SettledMegaRequest } from '../services/interfaces/settled-mega-request';
import type {
  ListSettledMegaResponse,
  SettledMega,
} from '../services/interfaces/settled-mega-response';
import type { VolumeSummaryMegaResponse } from '../services/interfaces/volume-summary-mega-response';
import type { VolumeSummaryRequest } from '../services/interfaces/volume-summary-request';
import { MegasService } from '../services/megas.service';
import { PassDataService } from '../services/pass-data.service';

@Component({
  standalone: false,
  selector: 'invd-settled-megas',
  templateUrl: './settled-megas.component.html',
  styleUrls: ['./settled-megas.component.scss'],
})
export class SettledMegasComponent implements OnInit {
  public duration = 3;
  public durationType = 'MONTHS';
  public settledSfFixMega: SettledMega;
  public settledSfArmMega: SettledMega;
  public settledMfFixMega: SettledMega;
  public settledMfArmMega: SettledMega;
  public isSingleFamilyFixTabSelected = true;
  public isSingleFamilyArmTabSelected = false;
  public isMultiFamilyFixTabSelected = false;
  public isMultiFamilyArmTabSelected = false;
  public showMessage: boolean;
  protected errorMessage: string;
  public selectedTimeSpan = 'Past 3 months';
  public timeSpanSelectionArray: string[];
  public timeSpanPlaceholder = 'Past 3 months';
  public isTimeSpanDropdownOpen = false;

  public presentSortedColumn = '';
  public presentSortedColumnIsAscending = false;
  public hasCreateOrEditMegaAccessFlag: boolean;
  public displayPagination = true;
  public config: PaginationInstance = {
    id: 'settled-mega-pagination',
    itemsPerPage: 50,
    currentPage: 1,
  };
  private volumeSummaryRes: any;
  public totalSettledVolumeSFFIXMEGA = 0;
  public totalSettledVolumeSFARMMEGA = 0;
  public totalSettledVolumeMFFIXMEGA = 0;
  public totalSettledVolumeMFARMMEGA = 0;
  public poolNumber = '';
  public downloadCollateralPoolLinkClicked = true;
  public disclaimerMessage = '';

  private router = inject(Router);
  public fmLoadingPageService = inject(FmLoadingPageService);
  private megaService = inject(MegasService);
  private userService = inject(UserService);
  private downloadXlsx = inject(DownloadXlsxService);
  private feeDisclaimerService = inject(FeeDisclaimerService);
  private passDataService = inject(PassDataService);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    this.hasCreateOrEditMegaAccessFlag = this.userService.hasCreateOrEditMegaAccessFlag;
    const req = {} as SettledMegaRequest;
    req.duration = this.duration;
    req.durationType = this.durationType;
    this.timeSpanSelectionArray = ['Past 3 months', 'Past 6 months', 'Past 12 months'];
    this.getSettledMegas(req);
    this.getMonthlyVolumeSummary();
  }

  public getSettledMegas(mReq: SettledMegaRequest): void {
    this.megaService.listSettledMega(mReq).subscribe({
      next: (data) => this.handelListSettledMegaServiceSuccess(data as ListSettledMegaResponse),
      error: (error) => this.handleAWSListSettledMegaServiceFail(error),
    });
  }

  public getMonthlyVolumeSummary(): void {
    const volumeSummary = {} as VolumeSummaryRequest;
    volumeSummary.currentMonthOffsetStart = 0;
    volumeSummary.currentMonthOffsetEnd = 0;
    this.megaService.monthlyVolumeSummary(volumeSummary).subscribe({
      next: (data) => this.handleMonthlyVolumeSummarySuccess(data as VolumeSummaryMegaResponse),
      error: (error) => this.handleAWSMonthlyVolumeSummaryMegaServiceFail(error),
    });
  }

  public handleMonthlyVolumeSummarySuccess(volumeSummary: VolumeSummaryMegaResponse) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.volumeSummaryRes = volumeSummary && volumeSummary.monthlyVolumeSummary[0].megaTypeDetail;
    this.volumeSummaryRes.forEach((item) => {
      if (item.megaType != null) {
        if (item.megaType === StringConstants.MEGA_TYPE_SINGLE_FAMILY_FIX) {
          this.totalSettledVolumeSFFIXMEGA = item.volumeInfo.settled;
        } else if (item.megaType === StringConstants.MEGA_TYPE_SINGLE_FAMILY_ARM) {
          this.totalSettledVolumeSFARMMEGA = item.volumeInfo.settled;
        } else if (item.megaType === StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX) {
          this.totalSettledVolumeMFFIXMEGA = item.volumeInfo.settled;
        } else {
          this.totalSettledVolumeMFARMMEGA = item.volumeInfo.settled;
        }
      }
    });
    this.cdr.markForCheck();
  }

  public handleAWSMonthlyVolumeSummaryMegaServiceFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public handelListSettledMegaServiceSuccess(listSettled: ListSettledMegaResponse) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    listSettled.settledMegas.forEach((item) => {
      if (item.megaType != null) {
        if (item.megaType === StringConstants.MEGA_TYPE_SINGLE_FAMILY_FIX) {
          this.settledSfFixMega = item;
        } else if (item.megaType === StringConstants.MEGA_TYPE_SINGLE_FAMILY_ARM) {
          this.settledSfArmMega = item;
        } else if (item.megaType === StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX) {
          this.settledMfFixMega = item;
        } else if (item.megaType === StringConstants.MEGA_TYPE_MULTI_FAMILY_ARM) {
          this.settledMfArmMega = item;
        }
      }
    });
    this.megaTypeSelected(this.returnMegaType());
    this.cdr.markForCheck();
  }

  public handleAWSListSettledMegaServiceFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.displayPagination = false;
    this.cdr.markForCheck();
  }

  public returnMegaType(): string {
    if (this.isSingleFamilyFixTabSelected) {
      return StringConstants.MEGA_TYPE_SINGLE_FAMILY_FIX;
    } else if (this.isSingleFamilyArmTabSelected) {
      return StringConstants.MEGA_TYPE_SINGLE_FAMILY_ARM;
    } else if (this.isMultiFamilyFixTabSelected) {
      return StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX;
    } else if (this.isMultiFamilyArmTabSelected) {
      return StringConstants.MEGA_TYPE_MULTI_FAMILY_ARM;
    }
  }

  public megaTypeSelected(type: string) {
    this.config = {
      id: 'settled-mega-pagination',
      itemsPerPage: 50,
      currentPage: 1,
    };

    if (type === StringConstants.MEGA_TYPE_SINGLE_FAMILY_FIX) {
      this.isSingleFamilyFixTabSelected = true;
      this.isSingleFamilyArmTabSelected = false;
      this.isMultiFamilyFixTabSelected = false;
      this.isMultiFamilyArmTabSelected = false;
      if (this.settledSfFixMega.megaList != null) {
        this.showMessage = false;
        if (this.settledSfFixMega.megaList.length > 50) {
          this.displayPagination = true;
        } else {
          this.displayPagination = false;
        }
      } else {
        this.errorMessage = this.settledSfFixMega.errorMessage;
        this.showMessage = true;
        this.displayPagination = false;
      }
    } else if (type === StringConstants.MEGA_TYPE_SINGLE_FAMILY_ARM) {
      this.isSingleFamilyFixTabSelected = false;
      this.isSingleFamilyArmTabSelected = true;
      this.isMultiFamilyFixTabSelected = false;
      this.isMultiFamilyArmTabSelected = false;
      if (this.settledSfArmMega.megaList != null) {
        this.showMessage = false;
        if (this.settledSfArmMega.megaList.length > 50) {
          this.displayPagination = true;
        } else {
          this.displayPagination = false;
        }
      } else {
        this.errorMessage = this.settledSfArmMega.errorMessage;
        this.showMessage = true;
        this.displayPagination = false;
      }
    } else if (type === StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX) {
      this.isSingleFamilyFixTabSelected = false;
      this.isSingleFamilyArmTabSelected = false;
      this.isMultiFamilyFixTabSelected = true;
      this.isMultiFamilyArmTabSelected = false;
      if (this.settledMfFixMega.megaList != null) {
        this.showMessage = false;
        if (this.settledMfFixMega.megaList.length > 50) {
          this.displayPagination = true;
        } else {
          this.displayPagination = false;
        }
      } else {
        this.errorMessage = this.settledMfFixMega.errorMessage;
        this.showMessage = true;
        this.displayPagination = false;
      }
    } else if (type === StringConstants.MEGA_TYPE_MULTI_FAMILY_ARM) {
      this.isSingleFamilyFixTabSelected = false;
      this.isSingleFamilyArmTabSelected = false;
      this.isMultiFamilyFixTabSelected = false;
      this.isMultiFamilyArmTabSelected = true;
      if (this.settledMfArmMega.megaList != null) {
        this.showMessage = false;
        if (this.settledMfArmMega.megaList.length > 50) {
          this.displayPagination = true;
        } else {
          this.displayPagination = false;
        }
      } else {
        this.errorMessage = this.settledMfArmMega.errorMessage;
        this.showMessage = true;
        this.displayPagination = false;
      }
    }
  }

  public toggleTimeSpanDropdown() {
    this.isTimeSpanDropdownOpen = !this.isTimeSpanDropdownOpen;
  }

  public closeTimeSpanDropdown() {
    this.isTimeSpanDropdownOpen = false;
  }

  public setSelectedTimeSpan(mSel) {
    this.isTimeSpanDropdownOpen = false;
    this.selectedTimeSpan = mSel;
    const req = {} as SettledMegaRequest;
    if (mSel.toString().indexOf('3') > 0) {
      this.duration = 3;
      req.duration = this.duration;
      req.durationType = this.durationType;
    } else if (mSel.toString().indexOf('6') > 0) {
      this.duration = 6;
      req.duration = this.duration;
      req.durationType = this.durationType;
    } else if (mSel.toString().indexOf('12') > 0) {
      this.duration = 12;
      req.duration = this.duration;
      req.durationType = this.durationType;
    }
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    this.getSettledMegas(req);
  }

  public navigatePageTo(pageURL) {
    if (this.isSingleFamilyFixTabSelected) {
      this.passDataService.setSettledMegaRecreateMegaType(
        StringConstants.MEGA_TYPE_SINGLE_FAMILY_FIX,
      );
    } else if (this.isSingleFamilyArmTabSelected) {
      this.passDataService.setSettledMegaRecreateMegaType(
        StringConstants.MEGA_TYPE_SINGLE_FAMILY_ARM,
      );
    } else if (this.isMultiFamilyFixTabSelected) {
      this.passDataService.setSettledMegaRecreateMegaType(
        StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX,
      );
    } else if (this.isMultiFamilyArmTabSelected) {
      this.passDataService.setSettledMegaRecreateMegaType(
        StringConstants.MEGA_TYPE_MULTI_FAMILY_ARM,
      );
    }
    this.router.navigate([pageURL]);
  }

  public sortByGridByColumnName(columnName: string): void {
    if (this.presentSortedColumn !== columnName) {
      this.presentSortedColumnIsAscending = true;
    } else {
      this.presentSortedColumnIsAscending = !this.presentSortedColumnIsAscending;
    }
    const isAscending = this.presentSortedColumnIsAscending ? 1 : -1;

    if (this.isSingleFamilyFixTabSelected) {
      this.settledSfFixMega.megaList.sort((a: any, b: any) => {
        const textA = a[columnName];
        const textB = b[columnName];
        if (typeof textA === 'string' && typeof textB === 'string') {
          return textA.toString().toUpperCase() < textB.toString().toUpperCase()
            ? -1 * isAscending
            : textA.toString().toUpperCase() > textB.toString().toUpperCase()
              ? 1 * isAscending
              : 0;
        } else if (typeof textA === 'number' && typeof textB == 'number') {
          return textA < textB ? -1 * isAscending : textA > textB ? 1 * isAscending : 0;
        }
      });
    } else if (this.isSingleFamilyArmTabSelected) {
      this.settledSfArmMega.megaList.sort((a: any, b: any) => {
        const textA = a[columnName];
        const textB = b[columnName];
        if (typeof textA === 'string' && typeof textB === 'string') {
          return textA.toString().toUpperCase() < textB.toString().toUpperCase()
            ? -1 * isAscending
            : textA.toString().toUpperCase() > textB.toString().toUpperCase()
              ? 1 * isAscending
              : 0;
        } else if (typeof textA === 'number' && typeof textB == 'number') {
          return textA < textB ? -1 * isAscending : textA > textB ? 1 * isAscending : 0;
        }
      });
    } else if (this.isMultiFamilyFixTabSelected) {
      this.settledMfFixMega.megaList.sort((a: any, b: any) => {
        const textA = a[columnName];
        const textB = b[columnName];
        if (typeof textA === 'string' && typeof textB === 'string') {
          return textA.toString().toUpperCase() < textB.toString().toUpperCase()
            ? -1 * isAscending
            : textA.toString().toUpperCase() > textB.toString().toUpperCase()
              ? 1 * isAscending
              : 0;
        } else if (typeof textA === 'number' && typeof textB == 'number') {
          return textA < textB ? -1 * isAscending : textA > textB ? 1 * isAscending : 0;
        }
      });
    } else if (this.isMultiFamilyArmTabSelected) {
      this.settledMfArmMega.megaList.sort((a: any, b: any) => {
        const textA = a[columnName];
        const textB = b[columnName];
        if (typeof textA === 'string' && typeof textB === 'string') {
          return textA.toString().toUpperCase() < textB.toString().toUpperCase()
            ? -1 * isAscending
            : textA.toString().toUpperCase() > textB.toString().toUpperCase()
              ? 1 * isAscending
              : 0;
        } else if (typeof textA === 'number' && typeof textB == 'number') {
          return textA < textB ? -1 * isAscending : textA > textB ? 1 * isAscending : 0;
        }
      });
    }

    this.presentSortedColumn = columnName;
  }

  downloadXLSX(data) {
    let fileName = '';

    if (!data) {
      return;
    }

    fileName = this.poolNumber + '_collateral_confirmed';

    this.downloadXlsx.generateMegaExcel(data, fileName);
    //this.fmJsonToExcelService.exportAsXLSX(jsonText, this.poolNumber + "_collateral_confirmed");
  }

  public downloadCollateralPool(settledMegaDownloadPoolObj: any): void {
    this.poolNumber = settledMegaDownloadPoolObj.poolNumber;

    if (this.downloadCollateralPoolLinkClicked) {
      this.megaService.downloadCollateralPool(this.poolNumber).subscribe({
        next: (data) => this.handledownloadCollateralPoolFromParentSuccess(data),
        error: (error) => this.handleAWSdownloadCollateralPoolFromParentServiceFail(error),
      });
    }
    this.downloadCollateralPoolLinkClicked = false;
  }

  private handledownloadCollateralPoolFromParentSuccess(response) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.downloadCollateralPoolLinkClicked = true;
    this.downloadXLSX(response);
    this.cdr.markForCheck();
  }

  private handleAWSdownloadCollateralPoolFromParentServiceFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.downloadCollateralPoolLinkClicked = true;
    this.cdr.markForCheck();
  }

  public openDisclosure(megaObj, settledMegaDownloadPoolObj: any): void {
    let url: string = StringConstants.SETTLED_LISTING_DEFAULT_DISCLOSURE_URL;
    let cusipAppend = '';
    let megaType = '';

    cusipAppend = megaObj.cusip;
    megaType = settledMegaDownloadPoolObj && settledMegaDownloadPoolObj.megaType;
    url =
      !isNullOrUndefined(settledMegaDownloadPoolObj) &&
      !isNullOrUndefined(settledMegaDownloadPoolObj.disclosureLocation)
        ? settledMegaDownloadPoolObj.disclosureLocation
        : url;

    url =
      !isNullOrUndefined(settledMegaDownloadPoolObj.disclosureLocation) &&
      (megaType === 'SFFIXMEGA' || megaType === 'SFARMMEGA')
        ? url + cusipAppend
        : url;

    const disclosureTab = window.open();
    disclosureTab.location.href = url;
  }

  public needsDisclaimer(prefixCode: string): boolean {
    if (this.feeDisclaimerService.needsDisclaimer(prefixCode)) {
      this.disclaimerMessage = StringConstants.SUPERS_PREFIX_FEE_DISCLAIMER;
      return true;
    }
    return false;
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
